<template>
  <div class="file-upload-container">
    <!-- 头部操作栏 -->
    <div class="header">
      <div class="file-count">
        {{ displayList.length }} / {{ fileLimit }} 个文件
      </div>
      <button class="upload-btn" @click="triggerFileInput" type="button">
        <svg viewBox="0 0 24 24" class="icon">
          <path d="M14,13V17H10V13H7L12,8L17,13M19.35,10.03C18.67,6.59 15.64,4 12,4C9.11,4 6.6,5.64 5.35,8.03C2.34,8.36 0,10.9 0,14A6,6 0 0,0 6,20H19A5,5 0 0,0 24,15C24,12.36 21.95,10.22 19.35,10.03Z" />
        </svg>
        上传文件
      </button>
    </div>

    <!-- 主体内容 -->
    <div class="body">
      <!-- 单个文件/图片模式 -->
      <div v-if="isSingleMode" class="single-mode">
        
        <!-- 上传区域 -->
        <div 
          v-if="!displayValue || isMultipleType"
          class="upload-area"
          :class="{ 'drag-over': dragOver, 'uploading': isUploading }"
          @dragover.prevent="handleDragOver"
          @dragleave="dragOver = false"
          @drop.prevent="handleDrop"
        >
          <input 
            type="file" 
            ref="fileInput"
            :accept="realAccept"
            @change="handleFileChange"
            :multiple="isMultipleType"
          />
          <div class="upload-prompt">
            <div class="background-prompt">
              拖拽文件或粘贴上传
            </div>
            <div class="progress-container" v-if="isUploading">
              <div class="progress-bar" :style="{ width: progress + '%' }"></div>
            </div>
          </div>
        </div>
        <div v-if="displayValue" class="file-preview">
          <!-- 图片预览 -->
          <div v-if="isImageType" class="image-preview">
            <img :src="displayValue" alt="Preview" />
            <div class="file-actions">
              <button @click="removeFile(0)" title="删除" class="delete-btn" type="button">
                <svg viewBox="0 0 24 24">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                </svg>
              </button>
            </div>
          </div>
          
          <!-- 文件预览 -->
          <div v-else class="file-item">
            <div class="file-info">
              <div class="file-name">{{ getFileName(displayValue) }}</div>
              <div v-if="fileStatus[0]" class="file-status" :class="fileStatus[0]">
                {{ statusText[fileStatus[0]] }}
              </div>
            </div>
            <div class="file-actions">
              <button @click="removeFile(0)" title="删除" class="delete-btn" type="button">
                <svg viewBox="0 0 24 24">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 多个文件/图片模式 -->
      <div v-else class="multi-mode">
        
        <!-- 上传区域 -->
        <div 
          v-if="displayList.length < fileLimit"
          class="upload-area"
          :class="{ 'drag-over': dragOver, 'uploading': isUploading }"
          @dragover.prevent="handleDragOver"
          @dragleave="dragOver = false"
          @drop.prevent="handleDrop"
        >
          <input 
            type="file" 
            ref="fileInput"
            :accept="realAccept"
            @change="handleFileChange"
            multiple
          />
          <div class="upload-prompt">
            <div class="background-prompt">
              拖拽文件或粘贴上传
            </div>
            <div class="progress-container" v-if="isUploading">
              <div class="progress-bar" :style="{ width: progress + '%' }"></div>
            </div>
          </div>
        </div>
        <div class="file-list">
          <div 
            v-for="(item, index) in displayList" 
            :key="item.id"
            class="file-item"
          >
            <!-- 文件预览 -->
            <div class="file-content image-preview">
              <img :src="filePreview(item)" alt="Preview" />
              <div class="file-info">
                <div class="file-name">{{ getFileName(item) }}</div>
                <div v-if="fileStatus[index]" class="file-status" :class="fileStatus[index]">
                  {{ statusText[fileStatus[index]] }}
                </div>
              </div>
              <div class="file-actions">
                <button @click="removeFile(index)" title="删除" class="delete-btn" type="button">
                  <svg viewBox="0 0 24 24">
                    <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'FileUpload',
  props: {
    type: {
      type: String,
      default: 'file',
      validator: value => ['image', 'images', 'file', 'files'].includes(value)
    },
    accept: String,
    limit: {
      type: Number,
      default: undefined
    },
    modelValue: [String, Array, Object],
    linkKey: {
      type: String,
      default: 'link'
    },
    upload: String,
    useCos: {
      type: Boolean,
      default: false
    },
    requestData: {
      type: Object,
      default: () => ({})
    },
    uploadMethod: Function
  },
  data() {
    return {
      dragOver: false,
      internalValue: this.modelValue,
      pasting: false,
      isUploading: false,
      progress: 0,
      fileStatus: {},
      uploadingFiles: [],
      fileIdCounter: 0,
      fileLimit: undefined
    };
  },
  computed: {
    isSingleMode() {
      return this.type === 'image' || this.type === 'file';
    },
    isMultipleType() {
      return this.type === 'images' || this.type === 'files';
    },
    isImageType() {
      return this.type === 'image' || this.type === 'images';
    },
    realAccept() {
      if (this.accept) return this.accept;
      return this.isImageType ? 'image/*' : '';
    },
    displayValue() {
      if (!this.internalValue) return this.isSingleMode ? '' : [];
      
      if (this.isSingleMode) {
        return typeof this.internalValue === 'object' 
          ? this.internalValue[this.linkKey] 
          : this.internalValue;
      }
      
      return Array.isArray(this.internalValue)
        ? this.internalValue
        : [this.internalValue];
    },
    displayList() {
      if (this.isSingleMode) {
        return this.displayValue ? [{ 
          id: 'single-file', 
          [this.linkKey]: this.displayValue 
        }] : [];
      }
      
      return this.displayValue.map((item, index) => ({
        id: `file-${index}-${this.fileIdCounter}`,
        ...(typeof item === 'object' ? item : { [this.linkKey]: item })
      }));
    },
    statusText() {
      return {
        'uploading': '上传中...',
        'success': '上传成功',
        'error': '上传失败',
        'removed': '已删除'
      };
    }
  },
  watch: {
    modelValue(newVal) {
      this.internalValue = newVal;
    },
    internalValue(newVal) {
      this.$emit('update:modelValue', newVal);
    }
  },
  created() {
    if (this.limit) {
      this.fileLimit = this.limit
    } else {
      this.fileLimit = ['image', 'file'].includes(this.type) ? 1 : 999;
    }
    console.log(this.fileLimit, this.type);
    
  },
  mounted() {
    document.addEventListener('paste', this.handlePaste);
  },
  unmounted() {
    document.removeEventListener('paste', this.handlePaste);
  },
  methods: {
    triggerFileInput() {
      const currentCount = this.isSingleMode 
        ? (this.displayValue ? 1 : 0) 
        : this.displayValue.length;
      if (currentCount > this.fileLimit) {
        this.$message.error('文件数量超过限制！');
        return;
      }
      this.$refs.fileInput.click();
    },
    
    getFileLink(item) {
      return item[this.linkKey] || item;
    },
    
    getFileName(file) {
      if (!file) return '';
      if (typeof file === 'object') {
        return file.name || file.filename || this.getFileLink(file);
      }
      return file.split('/').pop().split('\\').pop();
    },
    
    fileType(file) {
      var filePath = this.getFileName(file);
      if (!filePath) return 'file';
      
      const ext = this.getFileName(filePath).split('.').pop().toLowerCase();
      const iconMap = {
        pdf: 'pdf',
        doc: 'word',
        docx: 'word',
        xls: 'excel',
        xlsx: 'excel',
        ppt: 'powerpoint',
        pptx: 'powerpoint',
        zip: 'archive',
        rar: 'archive',
        jpg: 'image',
        jpeg: 'image',
        png: 'image',
        gif: 'image'
      };
      return iconMap[ext];
    },

    filePreview(item) {
      const type = this.fileType(item);
      const link = this.getFileLink(item);
      const imageTypeSrc = {
        pdf: require('@/assets/images/file_type/pdf.png'),
        word: require('@/assets/images/file_type/doc.png'),
        excel: require('@/assets/images/file_type/xls.png'),
        powerpoint: require('@/assets/images/file_type/ppt.png'),
        archive: require('@/assets/images/file_type/zip.png'),
        apk: require('@/assets/images/file_type/apk.png'),
        bin: require('@/assets/images/file_type/bin.png'),
        exe: require('@/assets/images/file_type/exe.png'),
        file: require('@/assets/images/file_type/file.png'),
        movie: require('@/assets/images/file_type/movie.png'),
        mp3: require('@/assets/images/file_type/mp3.png'),
      }
      if (type === 'image') {
        return link
      } else {
        return imageTypeSrc[type]
      }
    },
    
    handleDragOver() {
      this.dragOver = true;
    },
    
    handleDrop(e) {
      this.dragOver = false;
      this.processFiles(e.dataTransfer.files);
    },
    
    handleFileChange(e) {
      this.processFiles(e.target.files);
      e.target.value = ''; // 重置input
    },
    
    handlePaste(e) {
      if (this.pasting) return;
      
      const items = e.clipboardData.items;
      if (!items) return;
      
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          this.pasting = true;
          const type = items[i].type.split('/')[1]
          const blob = items[i].getAsFile();
          const file = new File([blob], 'pasted-image.'+type, { type: items[i].type });
          
          setTimeout(() => {
            this.processFiles([file]);
            this.pasting = false;
          }, 100);
          break;
        }
      }
    },
    
    processFiles(files) {
      if (!files.length) return;
      
      const validFiles = Array.from(files);
      
      if (!validFiles.length) {
        this.$message.error('文件类型不对或没有选择文件！');
        return;
      }
      
      const currentCount = this.isSingleMode 
        ? (this.displayValue ? 1 : 0) 
        : this.displayValue.length;
      
      const newFiles = validFiles.slice(0, this.fileLimit - currentCount);
      
      if (!newFiles.length) {
        this.$message.error('文件数量超过限制！');
        return;
      }
      
      if (this.upload || this.uploadMethod) {
        this.uploadFiles(newFiles);
      } else {
        // 如果没有上传配置，直接使用本地URL
        if (this.isSingleMode) {
          this.emitFileUpdate(URL.createObjectURL(newFiles[0]));
        } else {
          const currentFiles = Array.isArray(this.internalValue) 
            ? [...this.internalValue] 
            : this.internalValue ? [this.internalValue] : [];
          
          const updatedFiles = [
            ...currentFiles, 
            ...newFiles.map(file => URL.createObjectURL(file))
          ].slice(0, this.fileLimit);
          
          this.internalValue = updatedFiles;
        }
      }
    },
    
    async uploadFiles(files) {
      this.isUploading = true;
      this.progress = 0;
      
      try {
        const uploadResults = [];
        
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const index = this.isSingleMode ? 0 : this.displayValue.length + i;
          
          // 设置文件状态为上传中
          this.fileStatus[index] = 'uploading';
          
          let result, name;
          if (this.uploadMethod) {
            // 使用自定义上传方法
            result = await this.uploadMethod(file, this.requestData, (progress) => {
              this.progress = progress;
            });
          } else {
            // 使用默认上传方法
            const res = await this.defaultUpload(file, index);
            result = res.link
            name = res.name
          }
          
          uploadResults.push(result);
          
          // 更新文件状态
          this.fileStatus[index] = 'success';
          
          // 更新文件列表
          if (this.isSingleMode) {
            this.emitFileUpdate(result);
          } else {
            const currentFiles = Array.isArray(this.internalValue) 
              ? [...this.internalValue] 
              : this.internalValue ? [this.internalValue] : [];

            if (Array.isArray(currentFiles)) {
              if (currentFiles[0] && typeof currentFiles[0] == 'object') {
                const fmodel = currentFiles[0]
                const link_key = this.linkKey ? this.linkKey : fmodel.link ? 'link' : 'url'
                const name_key = fmodel.name ? 'name' : 'filename'
                const res = {}
                res[link_key] = result
                res[name_key] = name
                result = res
              }
            }
            
            currentFiles.push(result);
            this.internalValue = currentFiles.slice(0, this.fileLimit);
          }
          
          this.fileIdCounter++;
          
          // 触发change事件
          setTimeout(() => {
            this.$emit('change', {
              type: 'add',
              file: result,
              index: index
            });
          }, 100)
        }
        
        return uploadResults;
      } catch (error) {
        console.error('Upload failed:', error);
        this.$message.error('上传文件失败！请检查网络或上传链接是否正确。');
      } finally {
        this.isUploading = false;
        this.progress = 0;
      }
    },
    
    async defaultUpload(file, index) {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);
        
        // 添加额外数据
        for (const key in this.requestData) {
          formData.append(key, this.requestData[key]);
        }
        
        const config = {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.lengthComputable) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              this.progress = percent;
            }
          }
        };
        
        // COS上传处理
        if (this.useCos) {
          // 这里简化处理，实际COS上传需要特殊处理
          config.url = this.upload;
        } else {
          config.url = this.upload;
        }
        
        console.log(file);
        
        
        axios.post(config.url, formData, config)
          .then(response => {
            if (response.status === 200 && response.data) {
              const data = response.data.data || response.data;
              const link = data.url || data.link || data
              const name = file.name
              resolve({ link, name });
            } else {
              this.fileStatus[index] = 'error';
              reject(new Error('Upload failed'));
            }
          })
          .catch(error => {
            this.fileStatus[index] = 'error';
            reject(error);
          });
      });
    },
    
    emitFileUpdate(file) {
      this.internalValue = file;
    },
    
    removeFile(index) {
      const removedFile = this.isSingleMode 
        ? this.internalValue 
        : this.internalValue[index];
      
      if (this.isSingleMode) {
        this.internalValue = null;
      } else {
        const newValue = [...this.internalValue];
        newValue.splice(index, 1);
        this.internalValue = newValue;
      }
      
      // 设置文件状态为已删除
      this.fileStatus[index] = 'removed';
      
      // 触发change事件
      setTimeout(() => {
        this.$emit('change', {
          type: 'remove',
          file: removedFile,
          index: index
        });
      }, 200)
    }
  }
};
</script>

<style lang="scss">
.file-upload-container {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  font-family: 'Segoe UI', 'Microsoft YaHei', sans-serif;
  color: #333;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  // box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: #f5f7fa;
    border-bottom: 1px solid #dcdfe6;
    
    .file-count {
      font-size: 14px;
      color: #606266;
      font-weight: 500;
    }
    
    .upload-btn {
      display: flex;
      align-items: center;
      padding: 6px 12px;
      background: #409eff;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      transition: all 0.3s;
      
      &:hover {
        background: #66b1ff;
      }
      
      .icon {
        width: 18px;
        height: 18px;
        fill: white;
        margin-right: 6px;
      }
    }
  }

  .body {
    position: relative;
    padding: 16px;
    
    .upload-area {
      position: absolute;
      top: 16px;
      left: 16px;
      width: calc(100% - 32px);
      height: calc(100% - 32px);
      z-index: 0;
      border-radius: 6px;
      transition: all 0.3s ease;
      overflow: hidden;
      
      &.drag-over {
        border-color: #409eff;
        background-color: #ecf5ff;
        box-shadow: 0 0 8px rgba(64, 158, 255, 0.3);
      }
      
      &.uploading {
        border-color: #e6a23c;
        background-color: #fdf6ec;
      }
      
      input[type="file"] {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        opacity: 0;
      }
      
      .upload-prompt {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        align-items: flex-end;
        
        .background-prompt {
          font-size: 20px;
          color: #c0c4cc;
          font-weight: 500;
          opacity: 0.7;
          user-select: none;
        }
        
        .progress-container {
          width: 80%;
          height: 6px;
          background: #ebeef5;
          border-radius: 3px;
          overflow: hidden;
          margin-top: 20px;
          
          .progress-bar {
            height: 100%;
            background: #409eff;
            transition: width 0.3s ease;
          }
        }
      }
      
      .corner-upload-btn {
        position: absolute;
        bottom: 16px;
        right: 16px;
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: #409eff;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        transition: all 0.3s;
        
        &:hover {
          background: #66b1ff;
          transform: scale(1.05);
        }
        
        .icon {
          width: 24px;
          height: 24px;
          fill: white;
        }
      }
    }
    
    /* 单个模式样式 */
    .single-mode {
      min-height: 100px;
      .file-preview {
        position: relative;
        margin-bottom: 15px;
        border: 1px solid #dcdfe6;
        border-radius: 6px;
        overflow: hidden;
        background: #fff;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
        
        .image-preview {
          position: relative;
          padding: 10px;
          background: #f5f7fa;
          text-align: center;
          
          img {
            display: block;
            max-width: 100%;
            max-height: 200px;
            margin: 0 auto;
            border-radius: 4px;
          }
        }
        
        .file-item {
          padding: 15px;
          display: flex;
          align-items: center;
          
          .file-info {
            flex: 1;
            
            .file-name {
              font-size: 16px;
              font-weight: 500;
              margin-bottom: 5px;
              margin-top: 10px;
            }
            
            .file-status {
              font-size: 13px;
              padding: 3px 8px;
              border-radius: 3px;
              display: inline-block;
              
              &.uploading {
                color: #e6a23c;
                background: #fdf6ec;
              }
              &.success {
                color: #67c23a;
                background: #f0f9eb;
              }
              &.error {
                color: #f56c6c;
                background: #fef0f0;
              }
              &.removed {
                color: #909399;
                background: #f4f4f5;
              }
            }
          }
        }
      }
      
      .file-actions {
        position: absolute;
        top: 10px;
        right: 10px;
        
        .delete-btn {
          background: #ff5252;
          color: white;
          border: none;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.8;
          transition: all 0.3s;
          
          &:hover {
            opacity: 1;
            transform: scale(1.1);
          }
          
          svg {
            width: 18px;
            height: 18px;
            fill: white;
          }
        }
      }
    }

    /* 多个模式样式 */
    .multi-mode {
      .file-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
        gap: 16px;
        margin-bottom: 16px;
        z-index: 1;
        
        .file-item {
          position: relative;
          border: 1px solid #dcdfe6;
          border-radius: 6px;
          overflow: hidden;
          background: #fff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          transition: all 0.3s;
          
          &:hover {
            transform: translateY(-3px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            
            .file-actions {
              opacity: 1;
            }
          }
          
          .image-preview {
            position: relative;
            padding: 10px;
            background: #f5f7fa;
            text-align: center;
            
            img {
              display: block;
              max-width: 100%;
              max-height: 120px;
              margin: 0 auto;
              border-radius: 4px;
            }
            
            .file-status {
              font-size: 12px;
              padding: 3px 8px;
              border-radius: 3px;
              display: inline-block;
              margin-top: 8px;
              
              &.uploading {
                color: #e6a23c;
                background: #fdf6ec;
              }
              &.success {
                color: #67c23a;
                background: #f0f9eb;
              }
              &.error {
                color: #f56c6c;
                background: #fef0f0;
              }
              &.removed {
                color: #909399;
                background: #f4f4f5;
              }
            }
          }
          
          .file-content {
            padding: 15px 10px;
            text-align: center;
            
            .file-info {
              padding: 0 5px;
              
              .file-name {
                font-size: 15px;
                word-break: break-all;
                line-height: 1.4;
                max-height: 2.8em;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                margin-top: 10px;
              }
              
              .file-status {
                font-size: 12px;
                padding: 3px 8px;
                border-radius: 3px;
                display: inline-block;
                margin-top: 5px;
                
                &.uploading {
                  color: #e6a23c;
                  background: #fdf6ec;
                }
                &.success {
                  color: #67c23a;
                  background: #f0f9eb;
                }
                &.error {
                  color: #f56c6c;
                  background: #fef0f0;
                }
                &.removed {
                  color: #909399;
                  background: #f4f4f5;
                }
              }
            }
          }
          
          .file-actions {
            position: absolute;
            top: 5px;
            right: 5px;
            opacity: 0;
            transition: opacity 0.3s;
            
            .delete-btn {
              background: #ff5252;
              color: white;
              border: none;
              width: 26px;
              height: 26px;
              border-radius: 50%;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              opacity: 0.8;
              transition: all 0.3s;
              
              &:hover {
                opacity: 1;
                transform: scale(1.1);
              }
              
              svg {
                width: 18px;
                height: 18px;
                fill: white;
              }
            }
          }
        }
      }
    }
  }
}
</style>
